import React, { useRef, useState, useEffect } from 'react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ThreeScene from './ThreeScene';
import FileTree from './FileTree';
import { mm } from './units';
import GUI from 'lil-gui';
import codeFilesData from './codeFiles.json';

const MARSEILLAISE_URL =
  'https://www.mfiles.co.uk/downloads/la-marseillaise.mid';

import bellParkPier4k from './assets/envmaps/bell_park_pier_4k.exr';
import goldenGateHills2k from './assets/envmaps/golden_gate_hills_2k.exr';
import neonPhotostudio4k from './assets/envmaps/neon_photostudio_4k.exr';
import studioCountryHall4k from './assets/envmaps/studio_country_hall_4k.exr';

const App: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [midiFile, setMidiFile] = useState<File | undefined>();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(MARSEILLAISE_URL, { mode: 'cors' });
        const buf  = await resp.arrayBuffer();
        if (cancelled) return;
        setMidiFile(
          new File([buf], 'la-marseillaise.mid', { type: 'audio/midi' })
        );
      } catch (err) {
        console.error('Failed to fetch MIDI:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const envMaps: Record<string, string> = {
    'Bell Park Pier 4K': bellParkPier4k,
    'Golden Gate Hills 2K': goldenGateHills2k,
    'Neon Photostudio 4K': neonPhotostudio4k,
    'Studio Country Hall 4K': studioCountryHall4k,
  };

  const [bias,           setBias]           = useState(0.2);
  const [turns,          setTurns]          = useState(4);
  const [particleMode,   setParticleMode]   = useState(true);
  const [particleCount,  setParticleCount]  = useState(0);
  const [playMarseillaise, setPlayMarseillaise] = useState(false);
  const [selectedEnvMap, setSelectedEnvMap] = useState('Bell Park Pier 4K');
  const [selectedFile, setSelectedFile] = useState<string | null>('src/ThreeScene.tsx');

  useEffect(() => {
    const gui    = new GUI();
    const params = { bias, turns, particleMode, particleCount, playMarseillaise, selectedEnvMap };

    gui.add(params, 'bias', 0, 1, 0.01)
       .name('Equator Bias')
       .onChange((v: number) => setBias(v));

    gui.add(params, 'turns', 1, 10, 0.1)
       .name('Spiral Turns')
       .onChange((v: number) => setTurns(v));

    gui.add(params, 'particleMode')
       .name('Particle Mode')
       .onChange((v: boolean) => setParticleMode(v));

    gui.add(params, 'particleCount', 0, 100, 1)
       .name('# Particles')
       .onChange((v: number) => setParticleCount(v));

    gui.add(params, 'playMarseillaise')
       .name('Play Marseillaise')
       .onChange((v: boolean) => setPlayMarseillaise(v));

    gui.add(params, 'selectedEnvMap', Object.keys(envMaps))
       .name('Environment Map')
       .onChange((v: string) => setSelectedEnvMap(v));

    return () => gui.destroy();
  }, []);

  return (
    <PanelGroup direction="horizontal" style={{ height: '100vh' }}>
      <Panel defaultSize={30} minSize={5}>
        <div style={{ backgroundColor: '#1e1e1e', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px', borderBottom: '1px solid #333' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#fff' }}>Files</h3>
            <FileTree files={codeFilesData.fileTree as any} onFileSelect={setSelectedFile} />
          </div>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {selectedFile && (codeFilesData.fileContents as any)[selectedFile] ? (
              <>
                <div style={{ padding: '10px', borderBottom: '1px solid #333', backgroundColor: '#2d2d2d', color: '#fff', fontSize: '12px' }}>
                  {selectedFile}
                </div>
                <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
                  <SyntaxHighlighter
                    language={(codeFilesData.fileContents as any)[selectedFile].language}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      padding: '10px',
                      fontSize: '12px',
                      backgroundColor: '#1e1e1e',
                      whiteSpace: 'pre',
                      wordWrap: 'normal',
                      minWidth: 'max-content',
                    }}
                    showLineNumbers={true}
                    wrapLines={false}
                    wrapLongLines={false}
                  >
                    {(codeFilesData.fileContents as any)[selectedFile].content}
                  </SyntaxHighlighter>
                </div>
              </>
            ) : (
              <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
                Select a file to view its code
              </div>
            )}
          </div>
        </div>
      </Panel>
      
      <PanelResizeHandle style={{
        width: '2px',
        backgroundColor: '#444',
        cursor: 'col-resize',
      }} />
      
      <Panel 
        defaultSize={70}
        onResize={() => {
          // Trigger resize event so Three.js can update
          setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
        }}
      >
        <div style={{ position: 'relative', height: '100%' }}>
          <canvas
            ref={canvasRef}
            style={{
              width:  '100%',
              height: '100%',
              display:'block',
              margin: 0,
              padding: 0,
            }}
          />
          <ThreeScene
            canvasRef={canvasRef as React.RefObject<HTMLCanvasElement>}
            startNote={24}
            endNote={96}
            numKeys={500}
            gap={mm(1)}
            layoutMode="corkscrew"
            corkscrewBias={bias}
            corkscrewTurns={turns}
            midiFile={playMarseillaise ? midiFile : undefined}
            particleMode={particleMode}
            particleCount={particleCount}
            envMapPath={envMaps[selectedEnvMap]}
            envMapType="exr"
          />
        </div>
      </Panel>
    </PanelGroup>
  );
};

export default App;
